import cn from "classnames";

// MUI
import { Box, ClickAwayListener, IconButton, MenuItem, Select, Typography } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { FilterOptionsPill, OptionsPillProps } from "components/FilterOptionsPill";

// Hooks
import useSelectAndChips from "./useSelectAndChips";

// Enums
import icons from "enums/icons";

// Styles
import { SelectAndChipsStyles } from "./SelectAndChips.styles";

// Types
import { TElement } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteFunctionsLevels";
import { ChipEntity } from "types";

const SelectAndChips = ({
    type,
    label,
    icon,
    description,
    items,
    setItems,
    list,
    fieldPlaceholder,
}: {
    type: OptionsPillProps["filterId"];
    label: JSX.Element | string;
    icon: JSX.Element;
    description: JSX.Element | string;
    items: ChipEntity[];
    setItems: React.Dispatch<React.SetStateAction<ChipEntity[]>>;
    list: TElement[];
    fieldPlaceholder: string;
}): JSX.Element => {
    const classes = SelectAndChipsStyles();

    const { showPlusIcon, handleSelectChange, handleClickButtonPlus, handleBlur, handleRemoveItem } = useSelectAndChips(
        {
            type,
            items,
            setItems,
            list,
        }
    );

    return (
        <Box className={classes.mainWrapper}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                {icon}
                <Typography className={classes.labelTitle}>{label}</Typography>
            </Box>

            <Typography paragraph className={classes.narratives}>
                {description}
            </Typography>

            <Box className={classes.componentWrapper}>
                <Box className={classes.selectWrapper}>
                    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleBlur}>
                        {showPlusIcon ? (
                            <IconButton className={classes.buttonPlus} onClick={handleClickButtonPlus}>
                                <CustomIcon icon={icons.plus} />
                            </IconButton>
                        ) : (
                            <Select
                                id={`brief-${type}`}
                                displayEmpty
                                className={classes.selectField}
                                onChange={handleSelectChange}
                                MenuProps={{
                                    PaperProps: {
                                        className: classes.optionsList,
                                    },
                                }}
                                renderValue={(value) => {
                                    if (!value) {
                                        return <Typography color="text.disabled">{fieldPlaceholder}</Typography>;
                                    }

                                    const selected = list.find((item) => item.type === value);
                                    return selected ? (
                                        <FilterOptionsPill
                                            shouldShowFullLabels
                                            filterId={type}
                                            type={selected.type}
                                            label={selected.name}
                                            sx={{ width: "fit-content" }}
                                        />
                                    ) : null;
                                }}
                            >
                                {list.map((item) => {
                                    const selected = items.some((i) => i.key === item.id);
                                    return (
                                        <MenuItem
                                            disableGutters
                                            key={item.id}
                                            value={item.type}
                                            autoFocus={false}
                                            className={cn(classes.menuItem, {
                                                [classes.menuItemInactive]: selected,
                                            })}
                                        >
                                            <FilterOptionsPill
                                                filterId={type}
                                                shouldShowFullLabels
                                                type={item.type}
                                                label={item.name}
                                                className={classes.chipBaseStyle}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        )}
                    </ClickAwayListener>
                </Box>
                <Box className={classes.selectedChipsWrapper}>
                    {items.map((item) => (
                        <FilterOptionsPill
                            key={item.key}
                            shouldShowFullLabels
                            filterId={type}
                            type={item.value}
                            label={item.title}
                            className={classes.chipBaseStyle}
                            onDelete={() => handleRemoveItem(item)}
                            deleteIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default SelectAndChips;
